import dayjs from 'dayjs';
import SchemaTyped from 'rsuite/esm/Schema/Schema';
import type { CheckType } from 'schema-typed';
import {
  ConfigCondition,
  ExpirationApplicationQuestion,
  QuestionAnswerType,
} from 'src/types/configuration';

export const getValidatorConfigurationForAnswerType = (
  answerType?: QuestionAnswerType,
  isRequired = false
) => {
  const { StringType, NumberType } = SchemaTyped.Types;
  const validatorConfig: {
    validator?: CheckType<unknown, any>;
    defaultErrorMessage: string;
  } = {
    validator: undefined,
    defaultErrorMessage: '',
  };
  switch (answerType) {
    case 'positive_number':
      validatorConfig.validator = NumberType().min(0);
      validatorConfig.defaultErrorMessage =
        'Enter a positive number (0 or greater)';
      break;
    case 'boolean':
      validatorConfig.validator = StringType().addRule(
        (value) => value === 'Yes' || value === 'No'
      );
      validatorConfig.defaultErrorMessage = 'Select Yes or No to continue';
      break;
    case 'radio':
      validatorConfig.validator = StringType();
      validatorConfig.defaultErrorMessage = 'Select an option to continue';
      break;
    case 'select':
      validatorConfig.validator = StringType();
      validatorConfig.defaultErrorMessage = 'Select an option to continue';
      break;
    case 'date':
      validatorConfig.validator = StringType().addRule((value) =>
        dayjs(value).isValid()
      );
      validatorConfig.defaultErrorMessage = 'Enter a valid date';
      break;
    case 'string':
    default:
      validatorConfig.validator = StringType();
      validatorConfig.defaultErrorMessage = 'Enter a text response';
  }

  if (isRequired && validatorConfig.validator) {
    validatorConfig.validator = validatorConfig.validator.isRequired(
      'Complete this field to continue'
    );
  }
  return validatorConfig;
};

export const getConditionsConfiguration = (
  question: ExpirationApplicationQuestion
) => {
  const conditionDescriptions = question.conditions;
  const conditionConfigs = conditionDescriptions?.map(
    (condition: ConfigCondition) => {
      return {
        compareKey: condition.key,
        evaluationFn: mapConditionDescriptionToCallback(condition),
      };
    }
  );
  return conditionConfigs;
};

const mapConditionDescriptionToCallback = (description: ConfigCondition) => {
  switch (description.op) {
    case 'equal':
      return (val: string) => val === description.value;
    case 'notEqual':
      return (val: string) => val !== description.value;
    case 'jsonEq':
      return (val: string) =>
        JSON.stringify(val) === JSON.stringify(description.value);
    case 'notjsonEq':
      return (val: string) =>
        JSON.stringify(val) !== JSON.stringify(description.value);
    case 'isNumberUnset':
      return (val: number) => val === undefined || val === null;
    case 'isDefined':
      return (val: number) => val !== undefined && val !== null;
    case 'isTruthy':
      return (val: boolean) => val == description.value;
    case 'isFalsy':
      return (val: boolean) => val != description.value;
    case 'any':
      return (val: string) =>
        !Array.isArray(description.value) ||
        (Array.isArray(description.value) && description.value.includes(val));
    case 'noneOf':
      return (val: string) =>
        !Array.isArray(description.value) ||
        (Array.isArray(description.value) && !description.value.includes(val));
    case 'dateIsBefore':
      return (val: Date) =>
        val instanceof Date &&
        description.value instanceof Date &&
        val.getTime() < description.value.getTime();
    case 'dateIsAfter':
      return (val: Date) =>
        val instanceof Date &&
        description.value instanceof Date &&
        val.getTime() > description.value.getTime();
    case 'dateIsSame':
      return (val: Date) =>
        val instanceof Date &&
        description.value instanceof Date &&
        val.getTime() === description.value.getTime();
    case 'greaterThan':
      return (val: number) => val > (description.value as number);
    case 'greaterThanOrEqual':
      return (val: number) => val >= (description.value as number);
    case 'lessThan':
      return (val: number) => val < (description.value as number);
    case 'lessThanOrEqual':
      return (val: number) => val <= (description.value as number);
    default:
      return () => null;
  }
};
